import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchScheduledEmailPreview,
  type ScheduledEmailPreviewResult,
} from '../../lib/previewScheduledEmail';
import type { ScheduledEmailRow } from '../Subscribers/types';

type Props = {
  row: ScheduledEmailRow | null;
  /** Optional display context under the title. */
  subscriberLabel?: string | null;
  stepLabel?: string | null;
  onClose: () => void;
};

/**
 * Send-faithful HTML preview for a scheduled_emails row (Postmark wrap / template / manual).
 * Same edge function as automation step preview — does not send.
 */
export default function ScheduledEmailPreviewModal({
  row,
  subscriberLabel,
  stepLabel,
  onClose,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<ScheduledEmailPreviewResult | null>(null);

  useEffect(() => {
    if (!row) {
      setPreview(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setPreview(null);

    void (async () => {
      try {
        const result = await fetchScheduledEmailPreview({
          subject: row.subject,
          bodyHtml: row.body_html,
          postmarkTemplateAlias: row.postmark_template_alias,
          contentMode: row.content_mode ?? null,
          subtitle: row.subtitle ?? null,
        });
        if (!cancelled) setPreview(result);
      } catch (e: unknown) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : 'Preview failed');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally only re-fetch when the scheduled row identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose is stable enough for error path
  }, [row?.id]);

  useEffect(() => {
    if (!row) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [row, onClose]);

  if (!row) return null;

  const subject = preview?.subject?.trim() || row.subject?.trim() || '(no subject)';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Scheduled email preview"
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Email preview</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5" title={subject}>
              Subject: {subject}
            </p>
            {subscriberLabel || stepLabel ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 truncate">
                {[subscriberLabel, stepLabel].filter(Boolean).join(' · ')}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : preview ? (
            <>
              {preview.warning ? (
                <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 mb-4">
                  {preview.warning}
                </p>
              ) : null}
              <div
                className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white overflow-hidden prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: preview.html }}
              />
            </>
          ) : null}
        </div>

        <div className="flex justify-end px-6 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
